/**
 * Pin a property where it actually is.
 *
 * A district name puts a property somewhere inside a few square kilometres; a pin puts it on its
 * street. Click anywhere to drop it, drag it to correct it, or let the browser offer its own position.
 *
 * Tiles come from OpenStreetMap's public servers, which is free and needs no account. Their usage
 * policy asks for a real referrer and discourages heavy traffic, which a broker panel is not — but it
 * is the one thing to revisit before this is in front of many people at once. Swapping provider is the
 * `TILES` line and nothing else.
 */
import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, MapPin } from 'lucide-react';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui';

const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
/** Riyadh, where the first properties are. Only used until a pin exists. */
const RIYADH: [number, number] = [24.7136, 46.6753];

export interface LatLng {
  lat: number;
  lng: number;
}

/**
 * Leaflet's default marker icon is loaded from a path relative to the CSS, which a bundler rewrites and
 * then cannot find. Drawing the pin ourselves avoids the missing-image square entirely and matches the
 * brand rather than looking borrowed.
 */
const pin = L.divIcon({
  className: '',
  html: `<span style="display:block;width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:var(--accent);border:2px solid var(--accent-fg);box-shadow:0 1px 4px rgb(0 0 0 / .35)"></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

export function MapPicker({ value, onChange, height = 320 }: { value: LatLng | null; onChange: (v: LatLng) => void; height?: number }) {
  const { t } = useI18n();
  const box = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  const latest = useRef(onChange);
  latest.current = onChange;
  const [locating, setLocating] = useState(false);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!box.current || map.current) return;
    const m = L.map(box.current, { zoomControl: true, attributionControl: true }).setView(value ? [value.lat, value.lng] : RIYADH, value ? 16 : 11);
    L.tileLayer(TILES, { attribution: ATTRIBUTION, maxZoom: 19 }).addTo(m);
    m.on('click', (e: L.LeafletMouseEvent) => latest.current({ lat: e.latlng.lat, lng: e.latlng.lng }));
    // Leaflet gives the container arrow-key panning and +/- zoom for free, but nothing that drops a
    // pin — so with a keyboard alone the form could not be completed at all. Enter and Space place it
    // at the centre of the view, which is what the arrows have just been aiming.
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      e.preventDefault();
      const c = m.getCenter();
      latest.current({ lat: c.lat, lng: c.lng });
    };
    const container = m.getContainer();
    container.addEventListener('keydown', onKey);
    map.current = m;
    // Leaflet measures the container on creation; inside a panel that is still laying out, that
    // measurement is zero and the map renders as a grey strip until something resizes it.
    const settle = setTimeout(() => m.invalidateSize(), 50);
    return () => {
      clearTimeout(settle);
      // Removed with the map. Left bound, React's development double-mount kept a listener pointing at
      // a map that had already been torn down, and every keyboard pin-drop threw `_leaflet_pos` out of
      // Leaflet's `getPosition` — harmless to the pin, noise to any error reporter watching.
      container.removeEventListener('keydown', onKey);
      m.remove();
      map.current = null;
      marker.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = map.current;
    if (!m) return;
    if (!value) {
      marker.current?.remove();
      marker.current = null;
      return;
    }
    if (marker.current) {
      marker.current.setLatLng([value.lat, value.lng]);
      return;
    }
    const mk = L.marker([value.lat, value.lng], { icon: pin, draggable: true }).addTo(m);
    mk.on('dragend', () => {
      const p = mk.getLatLng();
      latest.current({ lat: p.lat, lng: p.lng });
    });
    marker.current = mk;
    m.setView([value.lat, value.lng], Math.max(m.getZoom(), 16));
  }, [value?.lat, value?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  const locate = () => {
    if (!navigator.geolocation) {
      setDenied(true);
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setLocating(false);
        setDenied(false);
        latest.current({ lat: p.coords.latitude, lng: p.coords.longitude });
      },
      // Refused, unavailable or timed out all land here. It used to return the button to its label and
      // say nothing at all, so a broker who had blocked location waited for something that never came.
      () => {
        setLocating(false);
        setDenied(true);
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div ref={box} style={{ height }} className="wasity-map w-full rounded-md border border-line overflow-hidden z-0" role="application" aria-label={t('broker.addProperty.mapLabel')} />
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="secondary" className="min-h-9 text-[14px]" onClick={locate} disabled={locating}>
          <Crosshair size={15} aria-hidden /> {locating ? t('broker.addProperty.locating') : t('broker.addProperty.useMyLocation')}
        </Button>
        {value ? (
          <span className="num text-[13px] text-muted inline-flex items-center gap-1.5">
            <MapPin size={14} aria-hidden />
            {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
          </span>
        ) : (
          <span className="text-[13px] text-muted">{t('broker.addProperty.tapToPin')}</span>
        )}
      </div>
      {denied ? <p className="text-[13px] text-warning">{t('broker.addProperty.locationRefused')}</p> : null}
    </div>
  );
}
